import React from "react";
import { Box, Button, Flex, Grid, HStack, Table, Tbody, Td, Text, Textarea, Th, Thead, Tr, VStack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  ADVANTAGE_ITEM_DEFS,
  AXIS_META,
  AXIS_ORDER,
  RATING_SCALE,
  RatingValue,
  SalesDataLinkageInputState,
} from "./types";
import { RatingIcon } from "./Common";

interface Props {
  state: SalesDataLinkageInputState;
  isEditable: boolean;
  namespace: string;
  onChange: (patch: Partial<SalesDataLinkageInputState>) => void;
}

const ratingOf = (v: RatingValue | null | undefined) => RATING_SCALE.find((r) => r.val === v);

const ConfirmStep: React.FC<Props> = ({ state, isEditable, namespace, onChange }) => {
  const { t } = useTranslation();
  const tr = (key: string) => t(`features.InvestmentScreening.${namespace}.salesDataLinkage.${key}`);

  return (
    <VStack align="stretch" spacing="16px">
      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap="16px">
        <Box bg="#fff" border="1px solid #e2e8f0" borderRadius="10px" p="16px">
          <Text fontWeight={700} mb="10px">
            {tr("label.axisSummary")}
          </Text>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>{tr("label.targetAxis")}</Th>
                <Th isNumeric>{tr("label.achieveRate")}</Th>
                <Th>{tr("label.axisEvaluation")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {AXIS_ORDER.map((k) => {
                const meta = AXIS_META[k];
                const included = state.axisInclude[k];
                const rating = ratingOf(state.axisEvals[k]);
                return (
                  <Tr key={k}>
                    <Td>{tr(`tab.${meta.labelKey}`)}</Td>
                    <Td isNumeric>{included ? `${meta.achieve.toFixed(1)}%` : "-"}</Td>
                    <Td color={rating?.color}>
                      {included ? (
                        rating ? (
                          <HStack spacing="6px">
                            <RatingIcon val={rating.val} size={14} />
                            <Text as="span">{tr(`ratingScale.${rating.labelKey}`)}</Text>
                          </HStack>
                        ) : (
                          "-"
                        )
                      ) : (
                        tr("label.notIncluded")
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>

        <Box bg="#fff" border="1px solid #e2e8f0" borderRadius="10px" p="16px">
          <Text fontWeight={700} mb="10px">
            {tr("label.advantageSummary")}
          </Text>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>{tr("label.axisEvaluation")}</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {ADVANTAGE_ITEM_DEFS.map((item) => {
                const rating = ratingOf(state.evals[item.key]);
                return (
                  <Tr key={item.key}>
                    <Td>{tr(`advantage.${item.labelKey}`)}</Td>
                    <Td color={rating?.color}>
                      {rating ? (
                        <HStack spacing="6px">
                          <RatingIcon val={rating.val} size={14} />
                          <Text as="span">{tr(`ratingScale.${rating.labelKey}`)}</Text>
                        </HStack>
                      ) : (
                        "-"
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </Grid>

      <Box bg="#fff" border="1px solid #e2e8f0" borderRadius="10px" p="16px">
        <Text fontWeight={700} mb="8px">
          {tr("label.overallRating")}
        </Text>
        <HStack spacing="8px" mb="20px">
          {RATING_SCALE.map((r) => {
            const selected = state.overallRating === r.val;
            return (
              <Button
                key={r.val}
                title={tr(`ratingScale.${r.labelKey}`)}
                aria-label={tr(`ratingScale.${r.labelKey}`)}
                w="46px"
                h="46px"
                minW="46px"
                p={0}
                fontSize="21px"
                fontWeight={700}
                borderRadius="11px"
                border="1.5px solid"
                isDisabled={!isEditable}
                borderColor={selected ? r.color : "#e2e8f0"}
                color={selected ? "#fff" : "#b0b8c4"}
                bg={selected ? r.color : "#fff"}
                _hover={{ bg: selected ? r.color : "#f8fafc" }}
                onClick={() => onChange({ overallRating: r.val as RatingValue })}
              >
                <RatingIcon val={r.val} size={20} />
              </Button>
            );
          })}
        </HStack>

        <Box mb="20px">
          <Text fontSize="13px" fontWeight={700} color="#16233f" mb="6px">
            {tr("label.qualitativeNoteLabel")}
          </Text>
          <Textarea
            value={state.overallNote}
            onChange={(e) => onChange({ overallNote: e.target.value })}
            isDisabled={!isEditable}
            placeholder={tr("label.qualitativeNotePlaceholder")}
            rows={5}
            fontSize="13px"
            bg="#fff"
            borderColor="#e2e8f0"
            borderRadius="8px"
          />
        </Box>

        <Text fontWeight={700} mb="6px">
          {tr("label.confidence")}：{state.confidence}%
        </Text>
        <Box px="2px">
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={state.confidence}
            disabled={!isEditable}
            onChange={(e) => onChange({ confidence: Number(e.target.value) })}
            style={{ width: "100%" }}
          />
          <Flex justify="space-between" fontSize="11px" color="#9aa0a6" mt="2px">
            <Text>0</Text>
            <Text>50</Text>
            <Text>100</Text>
          </Flex>
        </Box>
      </Box>
    </VStack>
  );
};

export default ConfirmStep;
